import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconPlayerPause } from "@tabler/icons-react";
import { useUpdateSuppressedDeliveryMethods } from "../../hooks/useWorkflows";
import type { SuppressedDeliveryMethod } from "../../services/workflows.service";

interface ManageSuppressedMethodsModalProps {
  onClose: () => void;
  availableMethods: SuppressedDeliveryMethod[];
  suppressedMethods: SuppressedDeliveryMethod[];
}

/**
 * Lets administrators and medical directors choose which delivery methods are
 * held. Checking a method stops its reports at Pending; unchecking releases
 * everything queued for it on the next scheduler pass.
 *
 * Mounted only while open, so the checkboxes always start from the current
 * server state and a cancelled edit never leaks into the next one.
 */
export function ManageSuppressedMethodsModal({
  onClose,
  availableMethods,
  suppressedMethods,
}: ManageSuppressedMethodsModalProps) {
  const suppressedKeys = useMemo(
    () => suppressedMethods.map((method) => method.key),
    [suppressedMethods]
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>(suppressedKeys);
  const updateMutation = useUpdateSuppressedDeliveryMethods();

  // Classify by status code — never surface a transport-layer message.
  const describeSaveFailure = useCallback((error: unknown): string => {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 403) {
      return "You don't have permission to change delivery settings.";
    }
    if (status === 400) {
      return "One of those methods is no longer configured. Refresh the page and try again.";
    }
    return "Couldn't save the change. Please try again.";
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const result = await updateMutation.mutateAsync(selectedKeys);
      notifications.show({
        title: result.changed ? "Delivery settings updated" : "No changes",
        message: result.methods.length
          ? `Delivery is now held for: ${result.methods
              .map((method) => method.label)
              .join(", ")}`
          : "All delivery methods are now sending.",
        color: result.methods.length ? "orange" : "green",
        autoClose: 5000,
      });
      onClose();
    } catch (error) {
      notifications.show({
        title: "Couldn't update delivery settings",
        message: describeSaveFailure(error),
        color: "red",
        autoClose: 6000,
      });
    }
  }, [describeSaveFailure, onClose, selectedKeys, updateMutation]);

  return (
    <Modal
      opened
      onClose={onClose}
      title="Manage held delivery methods"
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Reports queued for a held method stay at Pending and are never sent.
          Nothing is lost. Releasing a method sends everything it is holding.
        </Text>

        <Checkbox.Group value={selectedKeys} onChange={setSelectedKeys}>
          <Stack gap="sm">
            {availableMethods.map((method) => (
              <Checkbox
                key={method.key}
                value={method.key}
                label={method.label}
                color="orange"
              />
            ))}
          </Stack>
        </Checkbox.Group>

        {selectedKeys.length > 0 && (
          <Alert
            icon={<IconPlayerPause size={16} />}
            color="orange"
            variant="light"
            role="status"
          >
            {selectedKeys.length} method
            {selectedKeys.length === 1 ? "" : "s"} will be held. Signed reports
            will not reach recipients by these methods until released.
          </Alert>
        )}

        {selectedKeys.length === 0 && suppressedKeys.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            variant="light"
            role="status"
          >
            This releases every held report. Everything queued will start
            sending within a minute.
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="orange"
            onClick={handleSave}
            loading={updateMutation.isPending}
          >
            Save changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
